import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminSuratIjinTidakMasukPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [adminUsers, setAdminUsers] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Real data for absent permits
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);
  // Always attempt to fetch data - the API will handle auth checks
  // This prevents issues where the user object might not be loaded yet

  // Move fetchPermits outside useEffect to make it accessible for onRefresh callback
  const fetchPermits = async (dateFilterParam = dateFilter, statusFilterParam = statusFilter) => {
    // Debug user state
    setLoading(true);
  
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Construct URL with query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('per_page', itemsPerPage.toString());
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilterParam) params.append('date_filter', dateFilterParam);
      if (statusFilterParam) params.append('status_filter', statusFilterParam);
      
      // Use the imported authFetch instead of window.authFetch
      const res = await authFetch(`${apiBase}/api/admin/absent-permits?${params.toString()}`, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch permits: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      
      // Handle paginated response
      if (data.data && Array.isArray(data.data)) {
        // Paginated response
        setTotalPages(data.last_page || 1);
        setTotalItems(data.total || 0);
        const sourceData = data.data;
      } else {
        // Non-paginated response (fallback)
        const sourceData = data.data || data || [];
        setTotalPages(1);
        setTotalItems(sourceData.length);
      }
      
      const sourceData = data.data && Array.isArray(data.data) ? data.data : (data.data || data || []);
      
      // Map backend data to UI format for absent permits
      const mapped = sourceData.map(item => {
        const fromDate = item.from_date ? new Date(item.from_date.replace(/-/g, "/")) : null;
        const toDate = item.to_date ? new Date(item.to_date.replace(/-/g, "/")) : null;

        return {
          id: item.id,
          // Store both formatted date for display and ISO date for form inputs
          tanggalPengajuan: item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          // Keep original date in ISO format for date inputs
          tanggalPengajuanISO: item.created_at ? item.created_at.slice(0,10) : '',
          // Student name field is correct
          siswaMengajukan: item.student ? item.student.name : '',
          student: item.student,
          // Student has 'class' field, not 'kelas'
          kelas: item.student ? (item.student.class || '') : '',
          nis: item.student ? (item.student.nis || '') : '',
          // Absent permit specific fields
          tanggalMulai: item.from_date || '',
          tanggalSelesai: item.to_date || '',
          tanggalMulaiFormatted: fromDate ? fromDate.toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          tanggalSelesaiFormatted: toDate ? toDate.toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          keperluan: item.purpose || '',
          tempatTujuan: item.destination || '',
          penanggungjawab: item.pic || '',
          hubungan: item.relationship || '',
          nomorTelepon: item.pic_contact_number || '',
          // Use request_status field instead of status
          status: item.request_status || item.status || 'menunggu',
          // Add images array
          images: item.images || [],
          // Add activity field
          activity: item.activity || 'Teori',
          type: item.type || '',
          // Add decision_by fields
          decision_by: item.decision_by,
          decision_by_id: item.decision_by_id,
        };
      });
      setSuratIjinRequests(mapped);
    } catch (e) {
      setNotification({
        show: true,
        message: `Error fetching data: ${e.message}`,
        type: 'error'
      });
      setSuratIjinRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermits();
    
    // Fetch admin users for decision maker info
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin_users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(data.data || data);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();
    
    // eslint-disable-next-line
  }, [searchQuery, dateFilter, statusFilter, currentPage, itemsPerPage, user]);

  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student } });
    }
  };

  // Since we're using server-side pagination, we don't need client-side filtering or pagination
  // The API already handles filtering and pagination based on the query parameters
  const currentPageItems = suratIjinRequests;
  
  // Calculate display indices for pagination info
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilter) params.append('date_filter', dateFilter);
      if (statusFilter) params.append('status_filter', statusFilter);

      console.log('Making Excel export request to:', `/api/admin/absent-permits/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const response = await authFetch(`${apiBase}/api/admin/absent-permits/export-excel?${params.toString()}`, {
        method: 'GET',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Surat Ijin Tidak Masuk');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Surat_Ijin_Tidak_Masuk_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename} (${data.data.length} records)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Gagal mengekspor data ke Excel';
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Sesi telah berakhir. Silakan login kembali.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Tidak memiliki akses. Silakan login kembali.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Tidak memiliki izin untuk mengekspor data.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Endpoint ekspor tidak ditemukan. Hubungi administrator.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Terjadi kesalahan server. Coba lagi nanti.';
      } else if (error.message.includes('timeout') || error.message.includes('network')) {
        errorMessage = 'Koneksi timeout. Periksa koneksi internet Anda.';
      }
      
      setNotification({
        show: true,
        message: `${errorMessage} (Detail: ${error.message})`,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Pemberitahuan Tidak Masuk</h1>
            <Subtitle>Kelola dan tinjau permintaan surat ijin tidak masuk</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <ToolbarRow>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama guru, siswa, atau mata pelajaran..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                
                <FiltersContainer>
                  <FilterGroup>
                   
                    <FilterInput
                      type="date"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    />
                  </FilterGroup>
                  
                  <FilterGroup>
                   
                    <FilterSelect
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">Semua Status</option>
                      <option value="Menunggu">Menunggu</option>
                      <option value="Disetujui">Disetujui</option>
                      <option value="Ditolak">Ditolak</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <ExportButton onClick={handleExcelExport}>
                    📊 Export Excel
                  </ExportButton>
                </FiltersContainer>
              </ToolbarRow>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal Pengajuan</th>
                    <th>Siswa</th>
                    <th>Kelas</th>
                    <th>Dari</th>
                    <th>Sampai</th>
                    <th>Keperluan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{request.tanggalPengajuan}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.siswaMengajukan}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>

                        <td>{request.tanggalMulaiFormatted}</td>
                        <td>{request.tanggalSelesaiFormatted}</td>
                        <td>{request.keperluan}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.status === 'pending' || request.status === 'menunggu' ? 'Menunggu' :
                              request.status === 'approved' || request.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada permohonan ijin
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{endIndex} dari {totalItems} data
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          active={currentPage === pageNum}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <SuratIjinEditModal
              selectedPermit={selectedPermit}
              setSelectedPermit={setSelectedPermit}
              adminUsers={adminUsers}
              onRefresh={() => fetchPermits(dateFilter, statusFilter)}
            />
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Editable modal for Surat Ijin Tidak Masuk
function SuratIjinEditModal({ selectedPermit, setSelectedPermit, adminUsers, onRefresh }) {
  const [editPermit, setEditPermit] = React.useState(selectedPermit);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Helper function to get admin name
  const getDecisionMakerName = (permit) => {
    if (!permit) {
      return 'Admin';
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (permit.decision_by && permit.decision_by.name) {
      return permit.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (permit.decision_by_id && adminUsers.length > 0) {
      // Use '==' for loose comparison to avoid issues with string vs. number IDs.
      const admin = adminUsers.find(admin => admin.id == permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    return 'Admin';
  };
  
  React.useEffect(() => {
    setEditPermit(selectedPermit);
  }, [selectedPermit]);
  
  // Function to handle activity change
  const handleActivityChange = (activity) => {
    setEditPermit({ ...editPermit, activity });
  };
  
  // Function to update permit status
  const updatePermitStatus = async (status) => {
    if (!selectedPermit || !selectedPermit.id) return;

    if (!editPermit.type) {
      setNotification({
        show: true,
        message: 'Kategori Tidak Masuk wajib diisi.',
        type: 'error',
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const res = await authFetch(`${apiBase}/api/admin/absent-permits/${selectedPermit.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ 
          status,
          from_date: editPermit.tanggalMulai && editPermit.tanggalMulai.trim() !== '' ? editPermit.tanggalMulai : null,
          to_date: editPermit.tanggalSelesai && editPermit.tanggalSelesai.trim() !== '' ? editPermit.tanggalSelesai : null,
          purpose: editPermit.keperluan || null,
          destination: editPermit.tempatTujuan || null,
          pic: editPermit.penanggungjawab || null,
          relationship: editPermit.hubungan || null,
          pic_contact_number: editPermit.nomorTelepon || null,
          activity: editPermit.activity || 'Teori',
          type: editPermit.type || '',
        })
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
      }
      
      const updatedPermit = await res.json();
      console.log('Status updated:', updatedPermit);
      
      // Update the local state with the new status
      const updatedLocalPermit = {
        ...editPermit,
        status: status,
        request_status: status
      };
      
      setNotification({
        show: true,
        message: status === 'disetujui' ? 'Surat ijin berhasil disetujui dan data diperbarui!' : 'Surat ijin ditolak dan data diperbarui!',
        type: status === 'disetujui' ? 'success' : 'info'
      });
      
      // Close notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
        // Refresh the data while preserving filters
        if (onRefresh) {
          onRefresh();
        }
      }, 3000);
      
      // Update the permit in the parent component
      setSelectedPermit(updatedLocalPermit);
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        show: true,
        message: `Error: ${error.message}`,
        type: 'error'
      });
      
      // Close notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <>
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
      <form
        onSubmit={e => {
          e.preventDefault();
          // Form submission is now handled by the Terima/Tolak buttons
        }}
      >
      <DetailHeader>
        <DetailHeaderContent>
          <h2>Edit Pemberitahuan Tidak Masuk</h2>
          <StatusContainer>
            <DetailStatus status={selectedPermit.status}>
              {selectedPermit.status === 'pending' || selectedPermit.status === 'menunggu' ? 'Menunggu' :
                selectedPermit.status === 'approved' || selectedPermit.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
            </DetailStatus>
            {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
              <DecisionMaker>
                Diputuskan oleh: {getDecisionMakerName(selectedPermit)}
              </DecisionMaker>
            )}
          </StatusContainer>
        </DetailHeaderContent>
        <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
      </DetailHeader>

      <DetailContent>
        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Tanggal Pengajuan</DetailLabel>
              <input
                type="date"
                value={editPermit.tanggalPengajuanISO || ''}
                onChange={e => {
                  const newDate = e.target.value;
                  // Update both ISO and formatted date
                  setEditPermit({ 
                    ...editPermit, 
                    tanggalPengajuanISO: newDate,
                    tanggalPengajuan: newDate ? new Date(newDate).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : ''
                  });
                }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Siswa yang mengajukan</DetailLabel>
              <input
                type="text"
                value={editPermit.siswaMengajukan}
                onChange={e => setEditPermit({ ...editPermit, siswaMengajukan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kelas</DetailLabel>
              <input
                type="text"
                value={editPermit.kelas}
                onChange={e => setEditPermit({ ...editPermit, kelas: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>NIS</DetailLabel>
              <input
                type="text"
                value={editPermit.nis}
                onChange={e => setEditPermit({ ...editPermit, nis: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        {/* Activity Selection */}
        <DetailSection>
          <TypeToggleContainer>
            <TypeToggleLabel>Pilih Kelas</TypeToggleLabel>
            <TypeToggleButtonGroup>
              <TypeToggleButton 
                type="button"
                active={(editPermit.activity === 'Teori').toString()}
                onClick={() => handleActivityChange('Teori')}
              >
                Teori
              </TypeToggleButton>
              <TypeToggleButton 
                type="button"
                active={(editPermit.activity === 'Praktek').toString()}
                onClick={() => handleActivityChange('Praktek')}
              >
                Praktek
              </TypeToggleButton>
            </TypeToggleButtonGroup>
          </TypeToggleContainer>
        </DetailSection>

        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Tanggal Mulai</DetailLabel>
              <input
                type="date"
                value={editPermit.tanggalMulai || ''}
                onChange={e => setEditPermit({ ...editPermit, tanggalMulai: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Tanggal Selesai</DetailLabel>
              <input
                type="date"
                value={editPermit.tanggalSelesai || ''}
                onChange={e => setEditPermit({ ...editPermit, tanggalSelesai: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kategori Tidak Masuk</DetailLabel>
              <select
                value={editPermit.type || ''}
                onChange={e => setEditPermit({ ...editPermit, type: e.target.value })}
                required
              >
                <option value="" disabled>Pilih kategori</option>
                <option value="sakit">Sakit</option>
                <option value="ijin">Ijin</option>
              </select>
            </DetailGroup>
            <DetailGroup full>
              <DetailLabel>Ada keperluan apa</DetailLabel>
              <textarea
                value={editPermit.keperluan}
                onChange={e => setEditPermit({ ...editPermit, keperluan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup full>
              <DetailLabel>Nama tempat dan alamat tujuan</DetailLabel>
              <textarea
                value={editPermit.tempatTujuan}
                onChange={e => setEditPermit({ ...editPermit, tempatTujuan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nama penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.penanggungjawab}
                onChange={e => setEditPermit({ ...editPermit, penanggungjawab: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Hubungan</DetailLabel>
              <input
                type="text"
                value={editPermit.hubungan}
                onChange={e => setEditPermit({ ...editPermit, hubungan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nomor telepon penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.nomorTelepon}
                onChange={e => setEditPermit({ ...editPermit, nomorTelepon: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        {/* Images Section */}
        {selectedPermit.images && selectedPermit.images.length > 0 && (
          <DetailSection>
            <DetailLabel>Lampiran Gambar</DetailLabel>
            <ImagesGrid>
              {selectedPermit.images.map((image, index) => {
                // Handle different image formats similar to SicknessForm
                let imgSrc = '';
                if (typeof image === 'string') {
                  imgSrc = image;
                } else if (image && image.image_url) {
                  // API returns image objects with image_url property
                  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                  imgSrc = image.image_url.startsWith('http') 
                    ? image.image_url 
                    : `${baseUrl}/${image.image_url}`;
                }
                
                return (
                  <ImageContainer key={index}>
                    <img
                      src={imgSrc}
                      alt={`Lampiran ${index + 1}`}
                      onClick={() => window.open(imgSrc, '_blank')}
                      onError={(e) => {
                        console.error('Image failed to load:', imgSrc);
                        e.target.style.display = 'none';
                      }}
                    />
                  </ImageContainer>
                );
              })}
            </ImagesGrid>
          </DetailSection>
        )}

        <ActionButtons>
        <RejectButton 
            type="button" 
            onClick={() => updatePermitStatus('ditolak')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Tolak'}
          </RejectButton>
          <ApproveButton 
            type="button" 
            onClick={() => updatePermitStatus('disetujui')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Disetujui'}
          </ApproveButton>
          
        </ActionButtons>
      </DetailContent>
    </form>
    </>
  );
}

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;

  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-dark);
  }
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  outline: none;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  outline: none;
  cursor: pointer;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

// Activity toggle styled components
const TypeToggleContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TypeToggleLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TypeToggleButton = styled.button`
  background-color: ${props => props.active === 'true' ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active === 'true' ? 'white' : 'var(--text-dark)'};
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.active === 'true' ? 'var(--primary-light)' : 'var(--background-light)'};
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminSuratIjinTidakMasukPage;
